/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { CONTENT_MAX_WIDTH_AUTH } from '../../shared/layout/breakpoints';
import { AuthScreenFrame } from '../../shell/brand/AuthScreenFrame';
import { AppLogoMark } from '../../shell/brand/AppLogoMark';
import { ticketColors } from '../../shared/theme/ticketColors';
import { showInfo } from '../../shared/ui/appToast';
import { useAuthStore } from '../store/useAuthStore';
import { logout } from '../application/authSession';
import {
  abandonEmptyOwnedOrganization,
  setAwaitingEmployeeInvitation,
} from '../application/awaitingInvitation';
import { AUTH_ACCENT_INK, authFormUi } from './components/authFormUi';

type Props = {
  /** Vuelve a leer el bootstrap para ver si ya llegó la invitación. */
  onRefresh: () => void | Promise<void>;
};

/**
 * El usuario se registró pero va a entrar como empleado: espera que lo inviten
 * al mismo correo en vez de armar un negocio propio (B0-01).
 */
export const WaitingForInvitationScreen = ({ onRefresh }: Props) => {
  const email = useAuthStore((s) => s.session?.user?.email ?? null);
  const [checking, setChecking] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerConflict, setOwnerConflict] = useState(false);

  const busy = checking || switching || abandoning;

  const handleCheck = useCallback(async () => {
    setError(null);
    setChecking(true);
    try {
      await onRefresh();
      showInfo('Todavía nada', 'Cuando el dueño te invite, la vas a ver acá.');
    } finally {
      setChecking(false);
    }
  }, [onRefresh]);

  const handleBecomeOwner = useCallback(async () => {
    setError(null);
    setSwitching(true);
    try {
      const result = await setAwaitingEmployeeInvitation(false);
      if (!result.ok) {
        setOwnerConflict(result.code === 'already_owner');
        setError(result.message);
        return;
      }
      await onRefresh();
    } finally {
      setSwitching(false);
    }
  }, [onRefresh]);

  const handleAbandon = useCallback(async () => {
    setError(null);
    setAbandoning(true);
    try {
      const result = await abandonEmptyOwnedOrganization();
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setOwnerConflict(false);
      await onRefresh();
    } finally {
      setAbandoning(false);
    }
  }, [onRefresh]);

  return (
    <AuthScreenFrame>
      <View style={styles.root} accessibilityLabel="Esperando tu invitación">
        <View style={[authFormUi.card, styles.cardMax]}>
          <View style={authFormUi.brandBlock}>
            <AppLogoMark size={56} showWordmark />
          </View>

          <View style={styles.head}>
            <Text style={authFormUi.title}>Esperamos tu invitación</Text>
            <Text style={authFormUi.subtitle}>
              {email
                ? `Cuando te inviten a ${email}, la aceptás acá. No hace falta que armes un negocio.`
                : 'Cuando te inviten con este correo, la aceptás acá. No hace falta que armes un negocio.'}
            </Text>
          </View>

          {error ? (
            <View style={authFormUi.errorBanner} accessibilityLiveRegion="polite">
              <Text style={authFormUi.errorBannerText}>{error}</Text>
            </View>
          ) : null}

          <TouchableOpacity
            style={[authFormUi.primaryBtn, busy && authFormUi.primaryBtnDisabled]}
            onPress={() => void handleCheck()}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel="Revisar ahora"
            testID="awaiting-invite.check"
          >
            {checking ? (
              <ActivityIndicator color={AUTH_ACCENT_INK} />
            ) : (
              <Text style={[authFormUi.primaryLabel, busy && authFormUi.primaryLabelDisabled]}>
                Revisar ahora
              </Text>
            )}
          </TouchableOpacity>

          {ownerConflict ? (
            <TouchableOpacity
              style={styles.secondaryBtn}
              onPress={() => void handleAbandon()}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="Abandonar el negocio vacío"
              testID="awaiting-invite.abandon"
            >
              {abandoning ? (
                <ActivityIndicator color={ticketColors.ink2} />
              ) : (
                <Text style={styles.secondaryLabel}>Abandonar el negocio vacío</Text>
              )}
            </TouchableOpacity>
          ) : null}

          <View style={authFormUi.links}>
            <TouchableOpacity
              onPress={() => void handleBecomeOwner()}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="En realidad soy dueño, armar mi negocio"
              testID="awaiting-invite.become-owner"
            >
              {switching ? (
                <ActivityIndicator size="small" color={ticketColors.ink2} />
              ) : (
                <Text style={authFormUi.linkLabelAccent}>En realidad soy dueño — armar mi negocio</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => void logout()}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="Cerrar sesión"
              testID="awaiting-invite.logout"
            >
              <Text style={authFormUi.linkLabelMuted}>Cerrar sesión</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </AuthScreenFrame>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  cardMax: {
    maxWidth: CONTENT_MAX_WIDTH_AUTH,
  },
  head: {
    gap: 8,
  },
  secondaryBtn: {
    minHeight: 48,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
});
